<script lang="ts">
	/**
	 * `/Account/Subscriptions` — **Purchases**: everything the customer has bought, recurring or
	 * outright (Design §13, §14).
	 *
	 * The path and the title differ on purpose. `/Account/Subscriptions` is the path customers
	 * have bookmarked and the one every existing link points at, and a rename would break both to
	 * buy nothing; the title is what the page is *about*, and a subscription is something you
	 * purchased. `/Account/Purchases` resolves here too, for the same reason.
	 *
	 * Three sections on one page, in the order the questions get asked: **what am I paying for**,
	 * **what have I bought**, and **what did I used to pay for**. They were two pages and a toggle,
	 * and both halves of that were wrong. Subscriptions and one-time purchases are the same
	 * question to a customer — "what have I got from this store" — and splitting them across two
	 * nav items made a customer who could not remember which kind a product was check both. The
	 * toggle was worse: "Show ended subscriptions" is a control that hides half the answer by
	 * default and gives no hint that the hidden half exists, so a cancelled subscription read as a
	 * subscription that had never existed.
	 *
	 * **Ended is a section, not a filter.** It sits last because it is history, which is the same
	 * judgement the old default made — but a heading a customer can scroll to is not a switch they
	 * have to discover.
	 *
	 * The two halves page differently, on purpose. **Subscriptions load completely**, because a set
	 * split across two sections can only be split truthfully if all of it is in hand — paging them
	 * would put whichever rows happened to arrive first in one section and leave the other looking
	 * empty. There are only ever a handful, and the server holds them all in memory anyway.
	 * **Purchases keep the paged, infinite-scrolling list**, because that is the one of the two
	 * that genuinely grows without bound.
	 */

	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		EmptyState,
		InfiniteLoadElement,
		PageHeader,
		Panel,
		RefundButton,
		RoutedButton,
		RoutedLink,
		SkeletonWrapper,
		SubscriptionStatusBadge,
		TableRowsPlaceholder,
		ListRowsPlaceholder
	} from '../../components';
	import { link } from '../../svelte-spa-router';
	import { getAccountSummary, listAccountOrders, listAccountSubscriptions } from '../../AppNetwork';
	import { formatAmount, subscriptionPriceLine } from '../../stores/storefront';
	import { accessEndsAt, formatCalendarDate } from '../../Utilities';
	import type {
		AccountEntitlement,
		AccountOrder,
		AccountSubscription,
		AccountSummary
	} from '../../AppTypes';

	const SUBSCRIPTION_PAGE = 50;
	const ORDER_PAGE = 25;

	/**
	 * How many subscription pages to draw before giving up on completing the set.
	 *
	 * A backstop against a server whose `total` never agrees with what it returns, which would
	 * otherwise spin here forever. It is deliberately far above any real account, and being hit is
	 * **reported** rather than swallowed — a page that quietly stopped loading would put the
	 * missing rows in neither section, which is the failure this whole shape exists to avoid.
	 */
	const MAX_SUBSCRIPTION_PAGES = 20;

	/**
	 * The statuses filed under "Ended subscriptions". Everything else is in the active section, so
	 * a status cannot land in neither — this page loads with `includeInactive` and splits the set.
	 *
	 * `incomplete` is deliberately **not** here, although the server counts it inactive: it is a
	 * purchase whose payment has not cleared yet, and filing it under "ended" told a customer who
	 * had just paid that their subscription was over. It reads as pending, beside the live ones.
	 */
	const ENDED_STATUSES = ['canceled', 'expired'];

	let subscriptions: AccountSubscription[] = [];
	let subscriptionsLoading: boolean = true;
	let subscriptionsError: string = '';
	/** Set when the backstop above stops a set from completing. Never silent. */
	let subscriptionsTruncated: boolean = false;

	let orders: AccountOrder[] = [];
	let orderTotal: number = 0;
	let ordersLoading: boolean = true;
	let ordersLoadingMore: boolean = false;
	let ordersError: string = '';

	/**
	 * The customer and what they are entitled to — `GET /api/account/summary`.
	 *
	 * A third read, **guarded on its own**, because it fails for reasons unrelated to the two
	 * lists: what a customer holds must still be readable when a `COUNT(*)` over their orders has
	 * timed out, and the purchase history must not vanish because the summary did. The summary's
	 * own `subscriptions` field is deliberately ignored — it carries only the live ones, and the
	 * complete set is already in hand below.
	 */
	let summary: AccountSummary | null = null;
	let summaryLoading: boolean = true;
	let summaryError: string = '';

	$: customer = summary?.customer ?? null;
	$: entitlements = summary?.entitlements ?? [];

	/** The paging sentinel. Typed structurally — only its two re-arm methods are used here. */
	let sentinel: { resetVisibilityTracking: () => void; updateState: () => void } | undefined;

	/**
	 * A subscription that has ended.
	 *
	 * `cancelAtPeriodEnd` is deliberately **not** part of this: a subscription cancelled but still
	 * inside its paid period is one the customer still has access to, and filing it under history
	 * would tell them they had lost something they still hold. The badge says a cancellation is
	 * scheduled; the section says whether it is over.
	 */
	function hasEnded(subscription: AccountSubscription): boolean {
		return ENDED_STATUSES.includes(subscription.status);
	}

	$: active = subscriptions.filter(entry => !hasEnded(entry));
	$: ended = subscriptions.filter(hasEnded);

	/**
	 * Every subscription, current and past, in one complete set.
	 *
	 * `includeInactive` is always true here — the page shows both, and asking twice would be two
	 * round trips for one answer that the server assembles from one query either way.
	 */
	async function fnLoadSubscriptions(): Promise<void> {
		subscriptionsLoading = true;
		subscriptionsError = '';
		subscriptionsTruncated = false;
		const collected: AccountSubscription[] = [];
		const seen = new Set<string>();
		try {
			for (let page = 0; page < MAX_SUBSCRIPTION_PAGES; page += 1) {
				const result = await listAccountSubscriptions({
					includeInactive: true,
					limit: SUBSCRIPTION_PAGE,
					offset: collected.length
				});

				/*
				 * Deduplicated as it goes, and **a page that adds nothing new ends the set**.
				 *
				 * Both halves are about a server whose `total` does not agree with what it returns.
				 * Without the dedupe the loop appends the same rows over and over until the backstop
				 * trips, and `{#each … (uuid)}` throws on the duplicate keys long before it does.
				 * Without the no-progress exit it keeps asking. Testing instead for a page *shorter
				 * than the limit* would be simpler and wrong: it is the end of the set only for a
				 * server that always fills a page, and treating a short page as terminal would drop
				 * every row after it.
				 */
				const fresh = result.items.filter(item => !seen.has(item.uuid));
				for (const item of fresh) seen.add(item.uuid);
				collected.push(...fresh);

				if (collected.length >= result.total || fresh.length === 0) {
					subscriptions = collected;
					return;
				}
			}
			subscriptions = collected;
			subscriptionsTruncated = true;
		} catch (error) {
			subscriptionsError = (error as Error).message;
		} finally {
			subscriptionsLoading = false;
		}
	}

	async function fnLoadOrders(reset: boolean = true): Promise<void> {
		// The sentinel is re-armed after every append, and in a tall viewport it is still on
		// screen when that happens — so without this guard one scroll would keep paging past the
		// end of the list, appending the last page over and over.
		if (!reset && (ordersLoading || ordersLoadingMore || orders.length >= orderTotal)) return;

		if (reset) {
			ordersLoading = true;
			orders = [];
		} else {
			ordersLoadingMore = true;
		}
		ordersError = '';

		try {
			const result = await listAccountOrders({
				limit: ORDER_PAGE,
				offset: reset ? 0 : orders.length
			});
			orders = reset ? result.items : [...orders, ...result.items];
			orderTotal = result.total;
		} catch (error) {
			ordersError = (error as Error).message;
		} finally {
			ordersLoading = false;
			ordersLoadingMore = false;
			// Re-arm the sentinel: a short page on a tall viewport leaves it still on screen, and
			// without this it would never fire again and paging would stall silently.
			sentinel?.resetVisibilityTracking();
			sentinel?.updateState();
		}
	}

	async function fnLoadSummary(): Promise<void> {
		summaryLoading = true;
		summaryError = '';
		try {
			summary = await getAccountSummary();
		} catch (error) {
			summaryError = (error as Error).message;
		} finally {
			summaryLoading = false;
		}
	}

	onMount(() => {
		void fnLoadSummary();
		void fnLoadSubscriptions();
		void fnLoadOrders();
	});

	/**
	 * Through the shared helper, so a per-seat plan states its total rather than a unit price that
	 * is nowhere near what the customer pays (§9.7).
	 */
	function priceLine(subscription: AccountSubscription): string {
		return $subscriptionPriceLine(subscription);
	}

	function dateLine(subscription: AccountSubscription): string {
		const endsAt = accessEndsAt(subscription);
		if (endsAt !== null) {
			return `Access ends ${formatCalendarDate(endsAt)}`;
		}
		// A trial names its own end date rather than the renewal behind it: "Renews on the 14th" to
		// somebody who has not paid yet reads as a charge already agreed to.
		if (subscription.status === 'trialing' && subscription.trialEnd !== null) {
			return `Trial ends ${formatCalendarDate(subscription.trialEnd)}`;
		}
		if (subscription.status === 'canceled' || subscription.status === 'expired') {
			return subscription.canceledAt === null
				? `Started ${formatCalendarDate(subscription.createdAt)}`
				: `Ended ${formatCalendarDate(subscription.canceledAt)}`;
		}
		return subscription.currentPeriodEnd === null
			? ''
			: `Renews ${formatCalendarDate(subscription.currentPeriodEnd)}`;
	}

	/**
	 * One row per **thing the customer has**, not per entitlement key.
	 *
	 * A family is a set of tiers a customer moves between, so holding two keys in one family is a
	 * transition — an upgrade settling, an old tier still in grace — not two purchases. Listing
	 * both reads as having bought the same product twice, which is exactly the confusion this
	 * screen should not create.
	 *
	 * The group is represented by the **highest-ranked entitlement the customer still has access
	 * to**, falling back to the highest-ranked of any status when none is live: that is the tier
	 * they actually hold, and it is what the family's name should sit beside. A standalone product
	 * has no family and is its own group, so nothing changes for it.
	 */
	$: entitlementGroups = groupByFamily(entitlements);

	function groupByFamily(all: AccountEntitlement[]): AccountEntitlement[] {
		const groups = new Map<string, AccountEntitlement[]>();
		for (const entitlement of all) {
			const key =
				entitlement.familySlug === null
					? `key:${entitlement.entitlementKey}`
					: `family:${entitlement.familySlug}`;
			groups.set(key, [...(groups.get(key) ?? []), entitlement]);
		}

		return [...groups.values()].map(members => {
			const live = members.filter(member => member.status !== 'expired');
			return highestRanked(live.length > 0 ? live : members);
		});
	}

	function highestRanked(members: AccountEntitlement[]): AccountEntitlement {
		return members.reduce((best, member) =>
			(member.productRank ?? -1) > (best.productRank ?? -1) ? member : best
		);
	}

	/** The family's name where there is one — the group is the family, not the tier. */
	function entitlementTitle(entitlement: AccountEntitlement): string {
		return entitlement.familyName ?? entitlement.productName ?? entitlement.entitlementKey;
	}

	function entitlementLine(entitlement: AccountEntitlement): string {
		if (entitlement.status === 'grace' && entitlement.graceUntil !== null) {
			return `Access continues until ${formatCalendarDate(entitlement.graceUntil)} while a payment is sorted out`;
		}
		if (entitlement.status === 'expired') return 'Ended';
		if (entitlement.trialEnd !== null) return `Trial until ${formatCalendarDate(entitlement.trialEnd)}`;
		if (entitlement.expiresAt !== null) return `Renews ${formatCalendarDate(entitlement.expiresAt)}`;
		return 'Yours, with no end date';
	}

	/**
	 * Routes the card in-app, but only when it has somewhere to go.
	 *
	 * `use:link` reads the node's `href` and pushes it, so it is meaningless on the `<div>` an
	 * entitlement with no product slug renders as — a manual grant (§4.4) has no page to open.
	 * Applying it conditionally keeps one block of markup for both, instead of two copies of the
	 * card that can drift apart.
	 */
	function conditionalLink(node: HTMLElement, slug: string | null) {
		if (slug === null) return;
		return link(node as HTMLAnchorElement);
	}

	/** Text, never colour alone — a refunded order has to read as refunded in words. */
	const orderStatusCopy: Record<AccountOrder['status'], string> = {
		pending: 'Payment pending',
		paid: 'Paid',
		refunded: 'Refunded',
		partially_refunded: 'Partly refunded',
		failed: 'Payment failed'
	};
</script>

<PageHeader title="Purchases">
	<svelte:fragment slot="description">
		Everything you get from this store: what you have access to, what renews, what you bought
		outright, and where the paperwork is.
	</svelte:fragment>
</PageHeader>

<div class="space-y-6">
	{#if customer?.isFlagged}
		<!--
			A chargeback flag blocks new checkouts server-side (§9.6). Saying so here is the
			difference between a customer who contacts support and one who repeatedly tries to buy
			and is refused with no idea why.
		-->
		<div class="widget bg-depth border-widget">
			<div class="flex items-start gap-3">
				<i class="fa-solid fa-triangle-exclamation text-yellow-500 mt-1" />
				<div>
					<div class="font-semibold">New purchases are on hold on this account.</div>
					<p class="text-sm text-default">
						There is a disputed payment we need to resolve first. Existing access is unaffected, so
						get in touch and we'll sort it out.
					</p>
				</div>
			</div>
		</div>
	{/if}

	<!--
		Two columns from `xl`: **the account at a glance** beside **what you bought**.

		The rail carries the two compact panels — what the customer has access to, and where the
		paperwork is. The wide column carries the three that grow: subscriptions, one-time purchases
		and the ended ones. That split is by shape as much as by meaning: the one-time purchases
		table is five columns wide with a Refund button in the last of them, and squeezing it into a
		third of the page is the one change here that would cost more than it gained.

		`xl` rather than `lg` because the app sidebar already takes 256px: at `lg` the wide column
		would be about 480px, which is where that table stops being a table.

		Both cells carry `min-w-0`. A grid child's `min-width` defaults to `auto`, so the one-time
		purchases table sets the column's floor from its own min-content width and the *page* grows
		sideways rather than the table scrolling inside its `overflow-x-auto` wrapper — on a phone,
		where the table is widest relative to the screen and the horizontal scrollbar is hardest to
		notice. The page was block layout before this and had no such floor to escape.

		The rail is **first in the markup and second on screen**, swapped with `order` at the
		breakpoint. Below it there is one column, and a customer reads what they have before the
		list of what they bought — which is the question they arrive with. Doing it the other way
		round would put a year of purchases above the answer on every phone.
	-->
	<div class="grid gap-6 xl:grid-cols-3">
		<div class="min-w-0 space-y-6 xl:order-2">
	<!--
		What the customer holds, which is the question they arrive with — before what they pay for
		and long before the paperwork. Entitlement is not the same list as the subscriptions below:
		a one-time purchase grants one, a manual grant (§4.4) grants one with nothing behind it, and
		a subscription in grace still grants one while its payment is sorted out.
	-->
	<Panel title="What you have access to" faIcon="key">
		{#if summaryLoading}
			<SkeletonWrapper>
				<ListRowsPlaceholder rows={2} />
			</SkeletonWrapper>
		{:else if summaryError.length > 0}
			<EmptyState
				variant="error"
				title="What you have access to could not be loaded."
				description={summaryError}
			>
				<Button color="alternative" size="sm" on:click={() => void fnLoadSummary()}>Try again</Button>
			</EmptyState>
		{:else if entitlements.length === 0}
			<EmptyState title="Nothing yet." description="Anything you buy shows up here straight away.">
				<RoutedButton href="/" color="primary" size="sm">Browse the store</RoutedButton>
			</EmptyState>
		{:else}
			<ul class="space-y-2">
				{#each entitlementGroups as entitlement (entitlement.entitlementKey)}
					<li>
						<!--
							The whole card is the link when there is somewhere to go, and a real `<a>` —
							keyboard reachable, middle-clickable, and it shows the destination before the
							click.

							An entitlement with no product slug is not a link at all rather than a dead
							one: a manual grant (§4.4) has no page to open, and a card that looks
							clickable and does nothing is worse than a card that does not.
						-->
						<svelte:element
							this={entitlement.productSlug !== null ? 'a' : 'div'}
							use:conditionalLink={entitlement.productSlug}
							href={entitlement.productSlug !== null
								? `/Product/${entitlement.productSlug}`
								: undefined}
							class="widget bg-depth flex flex-wrap items-center gap-3 no-underline text-inherit {entitlement.productSlug !==
							null
								? 'bg-depth-hover'
								: ''}"
						>
							<div class="grow min-w-0">
								<div class="font-semibold truncate">{entitlementTitle(entitlement)}</div>
								{#if entitlement.familyName !== null && entitlement.productName !== null}
									<!-- The family names the thing; the tier says which of it they are on. -->
									<div class="text-xs text-default">{entitlement.productName}</div>
								{/if}
								<div class="text-sm text-default">{entitlementLine(entitlement)}</div>
							</div>
							<!-- Text, not a coloured dot: `grace` is access, and only the words say so. -->
							<span
								class="text-xs font-semibold uppercase tracking-wider {entitlement.status === 'expired'
									? 'text-default'
									: entitlement.status === 'grace'
										? 'text-yellow-600 dark:text-yellow-400'
										: 'text-green-600 dark:text-green-400'}"
							>
								{entitlement.status === 'grace'
									? 'Active, payment needed'
									: entitlement.status === 'active'
										? 'Active'
										: 'Ended'}
							</span>
							{#if entitlement.productSlug !== null}
								<i class="fa-solid fa-chevron-right text-default text-xs shrink-0" aria-hidden="true" />
							{/if}
						</svelte:element>
					</li>
				{/each}
			</ul>
		{/if}
	</Panel>

	<!--
		The paperwork, under what it is about. The figures are a summary and nothing is edited from
		this page — the two buttons go to the screens that do, which are also in the sidebar.

		Rendered only once the summary has arrived, and silently absent while it has not: a Billing
		panel with three empty fields states things about the account that are not true yet, and its
		failure is already reported by the access panel above rather than twice on one screen. The
		rail itself stays in the grid regardless, so the wide column does not resize under the reader
		when the summary lands.
	-->
	{#if customer !== null}
		<Panel title="Billing" faIcon="file-invoice">
			<!--
				Stacked, not three columns. This panel used to run the width of the page, where
				three columns read well; in the rail it is about 310px, and thirds of that wrap
				every label onto two lines and the currency line onto three.
			-->
			<div class="space-y-3 text-sm">
				<div>
					<div class="card-label mb-1">Billed in</div>
					{#if customer.currency}
						<!-- The pin (§10). Every later purchase uses it, so it is stated as a fact rather
						     than offered as a choice. -->
						<div>{customer.currency.toUpperCase()}, set by your first purchase</div>
					{:else}
						<div class="text-default">Not set yet. Your first purchase decides it.</div>
					{/if}
				</div>

				{#if customer.balance !== 0}
					<div>
						<div class="card-label mb-1">Account balance</div>
						<div>
							{$formatAmount(Math.abs(customer.balance), customer.currency) ?? 'Amount unavailable'}
							{customer.balance < 0 ? 'in credit' : 'owing'}
						</div>
					</div>
				{/if}

				{#if customer.billingEmail}
					<div class="min-w-0">
						<div class="card-label mb-1">Invoices go to</div>
						<div class="truncate">{customer.billingEmail}</div>
					</div>
				{/if}
			</div>

			<div class="mt-4 flex flex-wrap gap-2">
				<RoutedButton href="/Account/Billing" color="alternative" size="sm">
					Billing and payment methods
				</RoutedButton>
				<RoutedButton href="/Account/Invoices" color="alternative" size="sm">Invoices</RoutedButton>
			</div>
		</Panel>
	{/if}
		</div>

		<div class="min-w-0 space-y-6 xl:order-1 xl:col-span-2">
	<Panel title="Subscriptions" faIcon="rotate">
		<svelte:fragment slot="titleRight">
			{#if !subscriptionsLoading && subscriptionsError.length === 0}
				<span class="text-sm text-default">
					{active.length} active
				</span>
			{/if}
		</svelte:fragment>

		{#if subscriptionsLoading}
			<SkeletonWrapper>
				<ListRowsPlaceholder rows={3} />
			</SkeletonWrapper>
		{:else if subscriptionsError.length > 0}
			<EmptyState
				variant="error"
				title="Your subscriptions could not be loaded."
				description={subscriptionsError}
			>
				<Button color="alternative" size="sm" on:click={() => void fnLoadSubscriptions()}>
					Try again
				</Button>
			</EmptyState>
		{:else if active.length === 0}
			<EmptyState
				title="No active subscriptions."
				description="Anything you subscribe to appears here."
			>
				<RoutedButton href="/" color="primary" size="sm">Browse the store</RoutedButton>
			</EmptyState>
		{:else}
			<ul class="space-y-3">
				{#each active as subscription (subscription.uuid)}
					<li>
						<!--
							The whole card is the link, and it is a real `<a>` — keyboard reachable,
							middle-clickable, and it shows the destination before the click. Nothing
							inside may be a link or a button: nested interactive elements are invalid
							HTML and the inner one steals the click.
						-->
						<RoutedLink
							to={`/Account/Subscription/${subscription.uuid}`}
							class="widget bg-depth widget-spotlight flex flex-wrap items-center gap-4 no-underline text-inherit"
						>
							<div class="grow min-w-0">
								<div class="font-semibold">{subscription.productName}</div>
								<div class="text-sm text-default">
									{dateLine(subscription)}
									{#if priceLine(subscription).length > 0}
										· {priceLine(subscription)}
									{/if}
								</div>
								{#if subscription.pendingChange !== null}
									<div class="text-sm text-default">
										Changing to {subscription.pendingChange.productName} on
										{formatCalendarDate(subscription.pendingChange.effectiveAt)}
									</div>
								{/if}
							</div>
							<SubscriptionStatusBadge
								forCustomer
								status={subscription.status}
								cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
							/>
							<i class="fa-solid fa-chevron-right text-default text-xs shrink-0" aria-hidden="true" />
						</RoutedLink>
					</li>
				{/each}
			</ul>
		{/if}
	</Panel>

	<!--
		One-time purchases. Kept as its own section rather than mixed in with the subscriptions
		above: they answer the same question but behave nothing alike — one renews and can be
		cancelled, the other is done and can only be refunded.
	-->
	<!--
		"One-time purchases", not "Purchases": the page is called Purchases, and a section sharing
		its name would read as the whole of it — leaving the subscriptions above looking like
		something else. The qualifier is what makes the one-word page title safe.
	-->
	<Panel title="One-time purchases" faIcon="bag-shopping">
		<svelte:fragment slot="titleRight">
			<span class="text-sm text-default">{orderTotal} purchase{orderTotal === 1 ? '' : 's'}</span>
		</svelte:fragment>

		{#if ordersError.length > 0}
			<EmptyState variant="error" title="Your purchases could not be loaded." description={ordersError}>
				<Button color="alternative" size="sm" on:click={() => void fnLoadOrders()}>Try again</Button>
			</EmptyState>
		{:else if !ordersLoading && orders.length === 0}
			<EmptyState title="Nothing bought outright yet." description="One-time purchases show up here.">
				<RoutedButton href="/" color="primary" size="sm">Browse the store</RoutedButton>
			</EmptyState>
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Product</TableHeadCell>
					<TableHeadCell>Bought</TableHeadCell>
					<TableHeadCell>Total</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Refund</TableHeadCell>
				</TableHead>
				<TableBody>
					{#if ordersLoading}
						<TableRowsPlaceholder rows={4} columns={5} />
					{:else}
						{#each orders as order (order.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<RoutedLink to={`/Product/${order.productSlug}`} class="hover:text-primary-500">
										{order.productName}
									</RoutedLink>
									<!--
										Beside the name rather than in a column of its own (§4.1): a store
										that never sells by the unit would otherwise get a column of ones,
										and a customer who bought three of something has to be told so
										somewhere — the total alone reads as the wrong price.
									-->
									{#if (order.quantity ?? 1) > 1}
										<span class="text-default">&nbsp;× {order.quantity}</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{order.paidAt === null
										? formatCalendarDate(order.createdAt)
										: formatCalendarDate(order.paidAt)}
								</TableBodyCell>
								<!-- `tabular-nums` lines the column up and is what the stylesheet keys the
								     no-mid-value-break rule on: "€58.0 / 0" is a figure a customer can
								     misread on the screen where they decide whether to ask for money back. -->
								<TableBodyCell class="dataTable tabular-nums">
									{$formatAmount(order.total, order.currency) ?? 'Amount unavailable'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if order.status === 'pending'}
										<Badge color="blue" rounded title="Your payment is still being confirmed. Access starts as soon as it clears.">
											Pending — will be available soon
										</Badge>
									{:else}
										{orderStatusCopy[order.status]}
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<!-- A refund is issued against the invoice behind the order, which is why
									     the row carries an `invoiceUUID` at all. -->
									<RefundButton
										invoiceUUID={order.invoiceUUID}
										eligibility={order.refund}
										productName={order.productName}
										on:refunded={() => void fnLoadOrders()}
									/>
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					{/if}
				</TableBody>
			</Table>

			{#if !ordersLoading && orders.length < orderTotal}
				<InfiniteLoadElement
					bind:this={sentinel}
					class="w-full"
					on:becomeVisible={() => void fnLoadOrders(false)}
				>
					{#if ordersLoadingMore}
						<EmptyState variant="loading" compact title="Loading more…" />
					{/if}
				</InfiniteLoadElement>
			{/if}
		{/if}
	</Panel>

	<!--
		History, and only rendered when there is some. An "Ended subscriptions" heading over an
		empty state is a section telling a customer about something they have never done.
	-->
	{#if !subscriptionsLoading && subscriptionsError.length === 0 && ended.length > 0}
		<Panel title="Ended subscriptions" faIcon="clock-rotate-left">
			<svelte:fragment slot="titleRight">
				<span class="text-sm text-default">{ended.length}</span>
			</svelte:fragment>

			<ul class="space-y-3">
				{#each ended as subscription (subscription.uuid)}
					<li>
						<!--
							Dimmed, but still a link: an ended subscription is where the invoices for it
							are, and it is the row a customer opens when a charge they no longer expect
							turns up on a statement.
						-->
						<RoutedLink
							to={`/Account/Subscription/${subscription.uuid}`}
							class="widget bg-depth bg-depth-hover flex flex-wrap items-center gap-4 no-underline text-inherit opacity-75 hover:opacity-100"
						>
							<div class="grow min-w-0">
								<div class="font-semibold">{subscription.productName}</div>
								<div class="text-sm text-default">
									{dateLine(subscription)}
									{#if priceLine(subscription).length > 0}
										· {priceLine(subscription)}
									{/if}
								</div>
							</div>
							<SubscriptionStatusBadge
								forCustomer
								status={subscription.status}
								cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
							/>
							<i class="fa-solid fa-chevron-right text-default text-xs shrink-0" aria-hidden="true" />
						</RoutedLink>
					</li>
				{/each}
			</ul>

			{#if subscriptionsTruncated}
				<!-- Reported, never silent: rows the page could not load are rows in neither section. -->
				<p class="text-sm text-yellow-600 dark:text-yellow-400 mt-3">
					This account has more subscriptions than this page can show at once. Contact support if
					you need the full history.
				</p>
			{/if}
		</Panel>
	{/if}

		</div>
	</div>
</div>
